/**
 * Site Context — per-category comparators (Site_Context SC4).
 *
 * Pure functions, one per ClaimCategory. Each comparator receives:
 *   - the parsed ExtractedClaim,
 *   - the resolved SED value at the claim's `sedField`,
 * and returns a ClaimConflict (or null when there's no contradiction).
 *
 * Engineering rules (per CLAUDE.md):
 *   - Conservative — only fire on confident contradictions. Categories
 *     with no comparator pass through silently (claim still feeds the
 *     prompt block; no conflict surfaced).
 *   - Never throw. Missing / malformed SED values ⇒ "no conflict".
 *   - Dimensional analysis on every numeric comparator. Named constants
 *     for tolerances — no magic numbers.
 *
 * Severity tiers:
 *   - 'high'   ⇒ direct contradiction with high-confidence provider data.
 *     UI blocks submit until the user acknowledges or edits.
 *   - 'medium' ⇒ contradiction worth surfacing but not blocking (e.g.
 *     numeric deltas in the 10-25% band, or a regulatory-stance flip).
 *   - 'low'    ⇒ informational; doesn't gate submit.
 */
import type { ClaimConflict, ExtractedClaim, ClaimCategory } from '../../types/siteContext';

/**
 * Tolerance for parcel-acres delta. Below 10% ⇒ silent (geocoders +
 * parcel APIs disagree at this scale all the time). 10-25% ⇒ MEDIUM.
 * > 25% ⇒ HIGH (almost certainly a different parcel or a typo).
 */
const PARCEL_ACRES_MEDIUM_BAND = 0.10;
const PARCEL_ACRES_HIGH_BAND = 0.25;

/**
 * Substation-distance comparator: claim "co-located" / "on-site" vs SED
 * distance in km. SED ≥ this threshold (km) ⇒ contradiction.
 */
const SUBSTATION_COLOCATED_THRESHOLD_KM = 5;

export type Comparator = (
  claim: ExtractedClaim,
  sedValue: unknown,
) => ClaimConflict | null;

function makeConflict(
  claim: ExtractedClaim,
  claimedValue: string,
  sedValue: string,
  severity: ClaimConflict['severity'],
  explanation: string,
): ClaimConflict {
  return {
    id: `c-${claim.id}`,
    claimId: claim.id,
    sedField: claim.sedField ?? 'unknown',
    claimedValue,
    sedValue,
    severity,
    explanation,
  };
}

/* ── 1. power.grid ─────────────────────────────────────────────────── */

const compPowerGrid: Comparator = (claim, sedValue) => {
  // Claim asserts substation is "co-located" / "on-site" / "<1mi" but SED
  // distance is meaningfully larger. The reverse — claim says "distant"
  // when SED says "co-located" — doesn't surface as a conflict (provider
  // data wins on the optimistic side).
  if (typeof sedValue !== 'number' || !Number.isFinite(sedValue)) return null;
  const text = claim.claim.toLowerCase();
  const colocatedAssertion = /\b(co-?located|on[- ]site|adjacent|<\s*1\s*mi|less than 1 mile)\b/.test(text);
  if (colocatedAssertion && sedValue > SUBSTATION_COLOCATED_THRESHOLD_KM) {
    return makeConflict(
      claim,
      claim.claim,
      `${sedValue.toFixed(1)} km from substation`,
      'medium',
      `Claim asserts the substation is co-located / adjacent, but the SED records the nearest substation at ${sedValue.toFixed(1)} km.`,
    );
  }
  return null;
};

/* ── 2. power.btm ─────────────────────────────────────────────────── */

const compPowerBtm: Comparator = (claim, sedValue) => {
  // Claim asserts BTM nuclear feasibility but SED.nuclearStance is
  // 'Restrictive'. The state-level regulatory environment is the
  // load-bearing constraint here.
  if (typeof sedValue !== 'string') return null;
  const text = claim.claim.toLowerCase();
  const btmAssertion = /\bbtm\b/.test(text) && /(nuclear|smr|reactor)/.test(text);
  if (btmAssertion && /restrictive/i.test(sedValue)) {
    return makeConflict(
      claim,
      claim.claim,
      `State nuclear stance: ${sedValue}`,
      'high',
      `BTM nuclear is asserted but the state's regulatory stance is recorded as ${sedValue} — confirm a host-state exemption before relying on this path.`,
    );
  }
  return null;
};

/* ── 3. power.timeline ────────────────────────────────────────────── */

const compPowerTimeline: Comparator = (claim, sedValue) => {
  // Claim cites a near-term in-service quarter (e.g. "Q4 2026") but the
  // SED's queue-time estimate is "5yr+" / "long". Heuristic match — both
  // claim and SED are free-form strings here.
  if (typeof sedValue !== 'string') return null;
  const claimText = claim.claim.toLowerCase();
  const nearTerm = /q[1-4]\W?20(2[6-9]|3[0-5])\b/.test(claimText);
  const longQueue = /(5\s*yr\+|>\s*5\s*y|long|5\s*-\s*\d+\s*years)/i.test(sedValue);
  if (nearTerm && longQueue) {
    return makeConflict(
      claim,
      claim.claim,
      `Estimated queue time: ${sedValue}`,
      'medium',
      `Claim states a near-term in-service quarter, but the SED's interconnection-queue estimate is ${sedValue}.`,
    );
  }
  return null;
};

/* ── 4. water.cooling ─────────────────────────────────────────────── */

const compWaterCooling: Comparator = (claim, sedValue) => {
  // Claim asserts dry/air cooling, SED records High water stress —
  // informational; this is a "consistent with" not a contradiction.
  // Inverse — claim asserts wet cooling at high stress — IS surfaced.
  if (typeof sedValue !== 'string') return null;
  const claimText = claim.claim.toLowerCase();
  const wetCooling = /(wet|evaporative|once[- ]through|cooling tower)/.test(claimText);
  if (wetCooling && /high/i.test(sedValue)) {
    return makeConflict(
      claim,
      claim.claim,
      `Water stress: ${sedValue}`,
      'medium',
      `Claim plans wet cooling but the site is recorded as ${sedValue}-stress — water permits will be the long pole on schedule.`,
    );
  }
  return null;
};

/* ── 5. water.source ──────────────────────────────────────────────── */

const compWaterSource: Comparator = () => null; // No deterministic comparator yet; provider data is too sparse.

/* ── 6. water.rights ──────────────────────────────────────────────── */

const compWaterRights: Comparator = (claim, sedValue) => {
  // Claim asserts "water rights secured" — SED has water stress High +
  // no benchmark for rights. Surface as LOW for awareness, not blocker.
  if (typeof sedValue !== 'string') return null;
  const claimText = claim.claim.toLowerCase();
  if (/(rights|secured|allocation)/.test(claimText) && /high/i.test(sedValue)) {
    return makeConflict(
      claim,
      claim.claim,
      `Water stress: ${sedValue}`,
      'low',
      `Water rights are asserted as secured. SED notes the site sits in a ${sedValue}-stress basin — confirm allocation terms cover the proposed cooling load.`,
    );
  }
  return null;
};

/* ── 7. environmental.species ─────────────────────────────────────── */

const compEnvironmentalSpecies: Comparator = (claim, sedValue) => {
  // Claim "no ESA species concerns" but SED.environmental.species has
  // entries. The textual heuristic catches both "no ESA species" and
  // "no listed species" wordings.
  if (!Array.isArray(sedValue)) return null;
  const claimText = claim.claim.toLowerCase();
  const negatesSpecies = /\b(no|none|zero)\b.+\b(esa|listed|species|threatened|endangered)\b/.test(claimText);
  if (negatesSpecies && sedValue.length > 0) {
    return makeConflict(
      claim,
      claim.claim,
      `${sedValue.length} listed species`,
      'high',
      `Claim asserts no ESA species concerns, but the SED records ${sedValue.length} USFWS-listed species with ranges overlapping the site.`,
    );
  }
  return null;
};

/* ── 8. environmental.wetlands ───────────────────────────────────── */

const compEnvironmentalWetlands: Comparator = (claim, sedValue) => {
  // Claim "no wetlands" but SED.environmental.hasWetlands === true.
  if (typeof sedValue !== 'boolean') return null;
  const claimText = claim.claim.toLowerCase();
  const negatesWetlands = /\bno\b.*wetland/.test(claimText) || /\bzero\s+wetland/.test(claimText);
  if (negatesWetlands && sedValue === true) {
    return makeConflict(
      claim,
      claim.claim,
      'Wetlands present (USGS NWI hit)',
      'high',
      'Claim asserts no wetlands on site, but the USGS National Wetlands Inventory layer flags wetland geometry overlapping the parcel.',
    );
  }
  return null;
};

/* ── 9. environmental.nepa ────────────────────────────────────────── */

const compEnvironmentalNepa: Comparator = () => null; // Free-form text in SED; no deterministic match yet.

/* ── 10. environmental.tribal ─────────────────────────────────────── */

const compEnvironmentalTribal: Comparator = () => null; // No SED tribal-proximity field yet; pass through.

/* ── 11. environmental.phase1 ─────────────────────────────────────── */

const compEnvironmentalPhase1: Comparator = () => null; // Document-level; out of scope for SED cross-check.

/* ── 12. regulatory.zoning ────────────────────────────────────────── */

const compRegulatoryZoning: Comparator = (claim, sedValue) => {
  // Claim "industrial zoning" but SED.parcel.zoning is residential /
  // agricultural. Substring keyword check.
  if (typeof sedValue !== 'string') return null;
  const claimText = claim.claim.toLowerCase();
  const claimIndustrial = /\b(industrial|m-?1|m-?2|heavy\s+industrial)\b/.test(claimText);
  const sedResAg = /\b(residential|agricultural|r-?\d|farm)\b/i.test(sedValue);
  if (claimIndustrial && sedResAg) {
    return makeConflict(
      claim,
      claim.claim,
      `Zoning: ${sedValue}`,
      'high',
      `Claim implies industrial zoning, but the SED parcel record shows ${sedValue}. A zoning change or conditional-use approval would be required.`,
    );
  }
  return null;
};

/* ── 13. regulatory.permits ───────────────────────────────────────── */

const compRegulatoryPermits: Comparator = () => null; // SED has no canonical permits map yet.

/* ── 14. regulatory.air ───────────────────────────────────────────── */

const compRegulatoryAir: Comparator = () => null; // Air-basin override is a user preference, not a contradiction surface.

/* ── 15. geotechnical.foundation ──────────────────────────────────── */

const compGeotechnicalFoundation: Comparator = () => null; // Geotech is doc-driven; no SED counterpart yet.

/* ── 16. geotechnical.seismic ─────────────────────────────────────── */

const compGeotechnicalSeismic: Comparator = (claim, sedValue) => {
  // Claim "Site Class B" but SED.seismic.designCategory is D/E/F (high
  // hazard). Surface as MEDIUM — easy to mis-state, worth flagging.
  if (typeof sedValue !== 'string') return null;
  const claimText = claim.claim.toLowerCase();
  const lowHazardClaim = /\b(site\s+class\s+[ab]|category\s+[ab]|low\s+seismic)\b/.test(claimText);
  const highHazardSed = /[def]/i.test(sedValue) && sedValue.length <= 3;
  if (lowHazardClaim && highHazardSed) {
    return makeConflict(
      claim,
      claim.claim,
      `Seismic design category: ${sedValue}`,
      'medium',
      `Claim implies a low-hazard seismic context, but the SED records design category ${sedValue}. Confirm with the geotechnical report.`,
    );
  }
  return null;
};

/* ── 17. parcel.acres ─────────────────────────────────────────────── */

const compParcelAcres: Comparator = (claim, sedValue) => {
  if (typeof claim.claimedValue !== 'number' || typeof sedValue !== 'number') return null;
  if (!Number.isFinite(claim.claimedValue) || !Number.isFinite(sedValue)) return null;
  if (sedValue <= 0) return null;
  const diff = Math.abs(claim.claimedValue - sedValue) / sedValue;
  if (diff < PARCEL_ACRES_MEDIUM_BAND) return null;
  const severity: ClaimConflict['severity'] = diff > PARCEL_ACRES_HIGH_BAND ? 'high' : 'medium';
  return makeConflict(
    claim,
    `${claim.claimedValue} acres`,
    `${sedValue} acres (Regrid parcel)`,
    severity,
    `Claim of ${claim.claimedValue} acres differs from the SED parcel record of ${sedValue} acres by ${(diff * 100).toFixed(0)}%.`,
  );
};

/* ── 18. parcel.boundaries ────────────────────────────────────────── */

const compParcelBoundaries: Comparator = () => null; // Boundary geometry is in site.siteBoundary, not the SED.

/* ── 19. connectivity.fiber ───────────────────────────────────────── */

const compConnectivityFiber: Comparator = (claim, sedValue) => {
  // Claim "fiber on-site" or "≤1 mile" but nearest IX is far away.
  if (typeof sedValue !== 'number' || !Number.isFinite(sedValue)) return null;
  const claimText = claim.claim.toLowerCase();
  const onSite = /(on[- ]site|adjacent|<\s*1\s*mi|less than 1 mile|fiber on parcel)/.test(claimText);
  if (onSite && sedValue > 10) {
    return makeConflict(
      claim,
      claim.claim,
      `${sedValue.toFixed(1)} km to nearest IX`,
      'medium',
      `Claim implies on-site / adjacent fiber, but the nearest internet exchange is ${sedValue.toFixed(1)} km away.`,
    );
  }
  return null;
};

/* ── 20. connectivity.latency ─────────────────────────────────────── */

const compConnectivityLatency: Comparator = () => null; // Free-form claim; no SED-side latency benchmark to compare against.

/* ── 21. schedule.inservice ───────────────────────────────────────── */

const compScheduleInservice: Comparator = () => null; // Schedule claims have no SED counterpart.

/* ── 22. schedule.permitting ──────────────────────────────────────── */

const compSchedulePermitting: Comparator = () => null; // Same — no SED-side permit-timeline.

/* ── 23. finance.cost ─────────────────────────────────────────────── */

const compFinanceCost: Comparator = () => null; // No SED-side cost benchmark to compare against.

/* ── 24. finance.ppa ──────────────────────────────────────────────── */

const compFinancePpa: Comparator = () => null;

/* ── 25. technology.reactor ───────────────────────────────────────── */

const compTechnologyReactor: Comparator = () => null; // Tech choice is the user's call; nothing to contradict in the SED.

/* ── 26. technology.cooling ───────────────────────────────────────── */

const compTechnologyCooling: Comparator = () => null;

/* ── 27. technology.workload ──────────────────────────────────────── */

const compTechnologyWorkload: Comparator = () => null;

/* ── prior-study / doc-reference / commitment / unverifiable ──────── */

const compPriorStudy: Comparator = () => null;
const compDocReference: Comparator = () => null;
const compCommitment: Comparator = () => null;
const compUnverifiable: Comparator = () => null;

/**
 * Comparator map keyed by ClaimCategory. Use `Partial<Record<...>>` so
 * categories without comparators (most of them) silently pass through.
 */
export const CLAIM_COMPARATORS: Partial<Record<ClaimCategory, Comparator>> = {
  'power.grid': compPowerGrid,
  'power.btm': compPowerBtm,
  'power.timeline': compPowerTimeline,
  'water.cooling': compWaterCooling,
  'water.source': compWaterSource,
  'water.rights': compWaterRights,
  'environmental.species': compEnvironmentalSpecies,
  'environmental.wetlands': compEnvironmentalWetlands,
  'environmental.nepa': compEnvironmentalNepa,
  'environmental.tribal': compEnvironmentalTribal,
  'environmental.phase1': compEnvironmentalPhase1,
  'regulatory.zoning': compRegulatoryZoning,
  'regulatory.permits': compRegulatoryPermits,
  'regulatory.air': compRegulatoryAir,
  'geotechnical.foundation': compGeotechnicalFoundation,
  'geotechnical.seismic': compGeotechnicalSeismic,
  'parcel.acres': compParcelAcres,
  'parcel.boundaries': compParcelBoundaries,
  'connectivity.fiber': compConnectivityFiber,
  'connectivity.latency': compConnectivityLatency,
  'schedule.inservice': compScheduleInservice,
  'schedule.permitting': compSchedulePermitting,
  'finance.cost': compFinanceCost,
  'finance.ppa': compFinancePpa,
  'technology.reactor': compTechnologyReactor,
  'technology.cooling': compTechnologyCooling,
  'technology.workload': compTechnologyWorkload,
  'prior-study': compPriorStudy,
  'doc-reference': compDocReference,
  'commitment': compCommitment,
  'unverifiable': compUnverifiable,
};
